import { Link } from 'react-router';
import {
  DeltaIndicator,
  Headline,
  HeadlineNumber,
  MissingDataHint,
  buttonVariants,
  cn,
  formatDate,
  formatNumber,
  formatPercent,
} from '@mgdlead/ui';
import { PlugZap } from 'lucide-react';

import type { TodayData } from '../../../lib/api';

/**
 * G1 (07/10): a manchete do dia, em frase.
 *
 * "Hoje entraram **147** pessoas e **108** ficaram (73%). Ontem, nesse mesmo horário: 121."
 *
 * Nada aqui é conta (invariante 3): a taxa de quem ficou, a variação contra ontem e o "sem
 * medição" chegam prontos de `analytics.today`. Esta peça só escolhe a FRASE certa para o
 * estado — e são três estados que mandam fazer coisas diferentes:
 *
 *  - **sem medição** (algum grupo sem número conectado): "não sabemos" + Reconectar;
 *  - **zero** (medido e ninguém entrou): "ainda não entrou ninguém" — boa ou má notícia, mas
 *    é um número, não um buraco (invariante 8);
 *  - **normal**: entraram, ficaram, a taxa e a comparação com ontem até a mesma hora.
 */
export function MancheteDoDia({
  t,
  timezone,
  monitoresHref,
}: {
  t: TodayData;
  timezone: string;
  monitoresHref: string;
}) {
  const sem = t.semMedicao;
  const desde = sem ? desdeEmTexto(sem.desde, timezone, t.hoje) : null;
  const todosSemMedicao = sem !== null && sem.grupos >= t.grupos;

  if (sem !== null && todosSemMedicao) {
    return (
      <div className="flex flex-col gap-3">
        <Headline tone="brand">
          Não sabemos quantos entraram hoje: o número de monitoramento está desconectado
          {desde ? ` ${desde}` : ' agora'}.
        </Headline>
        <AvisoSemMedicao monitoresHref={monitoresHref} />
      </div>
    );
  }

  const entraram = t.fluxo.entraram;
  const delta =
    entraram > 0 && t.ontemMedido ? (
      <DeltaIndicator
        value={t.variacaoVsOntem}
        polarity="higher-is-better"
        comparedTo="vs ontem até esta hora"
        noBaseReason="ontem, até esta hora, ninguém tinha entrado — não há base para comparar"
        inverse
      />
    ) : null;

  return (
    <div className="flex flex-col gap-3">
      <Headline tone="brand" delta={delta}>
        {entraram === 0 ? (
          <>Hoje ainda não entrou ninguém.</>
        ) : (
          <>
            Hoje {entraram === 1 ? 'entrou' : 'entraram'}{' '}
            <HeadlineNumber>{formatNumber(entraram)}</HeadlineNumber>{' '}
            {entraram === 1 ? 'pessoa' : 'pessoas'} e{' '}
            <HeadlineNumber>{formatNumber(t.fluxo.ficaram)}</HeadlineNumber>{' '}
            {t.fluxo.ficaram === 1 ? 'ficou' : 'ficaram'}
            {t.fluxo.taxaFicaram !== null ? <> ({formatPercent(t.fluxo.taxaFicaram, 0)})</> : null}.
          </>
        )}{' '}
        <FraseDeOntem t={t} />
      </Headline>
      {sem !== null ? (
        <p className="text-sm text-primary-foreground/80">
          {sem.grupos === 1 ? '1 grupo está' : `${formatNumber(sem.grupos)} grupos estão`} sem
          número conectado{desde ? ` ${desde}` : ''}: quem entrar neles só aparece aqui depois que o
          número voltar.
        </p>
      ) : null}
      {sem !== null ? <AvisoSemMedicao monitoresHref={monitoresHref} /> : null}
    </div>
  );
}

/** A segunda frase: ontem até esta mesma hora. Sem medição ontem, não inventa "nenhuma". */
function FraseDeOntem({ t }: { t: TodayData }) {
  if (!t.ontemMedido)
    return <span className="font-normal">Ontem, nesse horário, ainda não medíamos.</span>;
  const ontem = t.entradasOntemAteAgora;
  if (t.fluxo.entraram === 0) {
    return (
      <span className="font-normal">
        {ontem === 0 ? (
          'Ontem, até esta hora, também ninguém.'
        ) : (
          <>
            Ontem, até esta hora, {ontem === 1 ? 'tinha entrado' : 'tinham entrado'}{' '}
            <HeadlineNumber>{formatNumber(ontem)}</HeadlineNumber>.
          </>
        )}
      </span>
    );
  }
  return (
    <span className="font-normal">
      Ontem, nesse mesmo horário: <HeadlineNumber>{formatNumber(ontem)}</HeadlineNumber>.
    </span>
  );
}

/** G6: o "—" do monitor caído, com o botão que resolve. */
function AvisoSemMedicao({ monitoresHref }: { monitoresHref: string }) {
  return (
    <MissingDataHint
      tone="brand"
      icon={PlugZap}
      action={
        <Link
          to={monitoresHref}
          className={cn(
            buttonVariants({ variant: 'secondary', size: 'sm' }),
            'min-h-11 md:min-h-0',
          )}
        >
          Reconectar
        </Link>
      }
    >
      Sem medição enquanto o número de monitoramento estiver desconectado.
    </MissingDataHint>
  );
}

/**
 * "desde as 9h" / "desde as 9h30" — no FUSO DO WORKSPACE (invariante 10). Se caiu antes de
 * hoje, a data entra junto ("desde 09/10 às 22h"). `null` sem horário conhecido: a frase não
 * inventa a hora.
 */
export function desdeEmTexto(iso: string | null, timezone: string, hoje: string): string | null {
  if (iso === null) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  const partes = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(data)
      .map((p) => [p.type, p.value]),
  ) as Record<string, string>;
  const dia = `${partes.year}-${partes.month}-${partes.day}`;
  const hora = Number(partes.hour);
  const minuto = partes.minute === '00' ? '' : partes.minute;
  const horaTexto = `${hora}h${minuto}`;
  if (dia === hoje) return `desde as ${horaTexto}`;
  const diaTexto = formatDate(data, { day: '2-digit', month: '2-digit', timeZone: timezone });
  return `desde ${diaTexto} às ${horaTexto}`;
}
